// src/components/NavbarComponents/MobileMenu.tsx
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, ArrowRight, ChevronDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NavLink } from '@/types/nav';

interface MobileMenuProps {
  isOpen: boolean;
  navLinks: NavLink[];
  onLinkClick: () => void;
}

const MobileMenu = ({ isOpen, navLinks, onLinkClick }: MobileMenuProps) => {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const location = useLocation();

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleDropdown = (name: string) => {
    if (activeDropdown === name) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(name);
    }
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onLinkClick();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="fixed inset-0 bg-stone-900 z-50 lg:hidden pt-20"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: '100vh' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex flex-col h-full overflow-y-auto pb-20">
            {/* Close Button */}
            <div className="absolute top-4 right-4 z-50">
              <button
                onClick={handleCloseClick}
                aria-label="Zapri meni"
                className="text-white bg-stone-800 p-2 rounded-md border border-white hover:bg-stone-700 hover:border-white active:bg-stone-600 active:scale-95 transition-all duration-200 shadow-[0_0_8px_rgba(255,255,255,0.4)]"
              >
                <X size={24} />
              </button>
            </div>

            {navLinks.map((link) => (
              <div key={link.name} className="py-2 border-b border-stone-800">
                <div 
                  className={`flex items-center justify-between px-4 py-2 ${
                    isActive(link.path) ? 'text-secondary font-bold' : 'text-white font-semibold'
                  }`}
                  onClick={() => link.hasDropdown && toggleDropdown(link.name)}
                >
                  <Link 
                    to={link.path} 
                    className="flex-1"
                    onClick={(e) => {
                      if (link.hasDropdown) {
                        e.preventDefault();
                      } else {
                        onLinkClick();
                      }
                    }}
                  >
                    {link.name}
                  </Link>
                  {link.hasDropdown && (
                    <ChevronDown 
                      size={20} 
                      className={`transition-transform duration-300 ${
                        activeDropdown === link.name ? 'rotate-180' : ''
                      }`} 
                    />
                  )}
                </div>
                
                {link.hasDropdown && activeDropdown === link.name && (
                  <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="pl-8 mt-1 space-y-1 bg-stone-800/80 py-2"
                  >
                    {/* Use the custom overview name */}
                    <Link 
                      to={link.overview?.path || link.path}
                      className="block px-4 py-2 text-sm text-white hover:text-secondary transition-colors font-medium"
                      onClick={onLinkClick}
                    >
                      {link.overview?.name || `Pregled ${link.name.toLowerCase()}`}
                    </Link>
                    {link.dropdownItems?.map((item) => (
                      <Link 
                        key={item.name} 
                        to={item.path}
                        className="block px-4 py-2 text-sm text-white hover:text-secondary transition-colors"
                        onClick={onLinkClick}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </div>
            ))}
            
            {/* Mobile CTA Buttons */}
            <div className="mt-auto px-4 py-6 space-y-3 bg-stone-800">
              <Button 
                variant="outline" 
                className="w-full flex items-center justify-center gap-2 border-white text-white hover:bg-white/20 font-semibold bg-transparent"
                onClick={() => window.location.href = 'tel:031467272'}
              >
                <Phone size={16} /> 031 467 272
              </Button>
              <Link 
                to="/kontakt"
                className="w-full inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-sm h-9 px-4 py-2"
                onClick={onLinkClick}
              >
                Zahtevaj ponudbo <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileMenu;